<template>
    <MainContainer>
        <div class="category">
            <h2>
                Kategorien
                <b-button
                    v-if="checkRight('write')"
                    :size="size"
                    @click="dataCreate">
                    Neu
                    <font-awesome-icon icon="plus" />
                </b-button>
            </h2>
            <section class="category-filter my-3">
                <b-row>
                    <b-col cols="4">
                        <b-form-group
                            :label-size="size"
                            label="Gruppen filtern"
                        >
                            <b-input-group>
                                <b-select
                                    v-model="filter._group_id"
                                    :options="groups.options"
                                    :size="size"
                                    :disabled="groups.busy"
                                    @change="updateFilter"
                                ></b-select>

                                <template #append>
                                    <b-button
                                        :size="size"
                                        variant="primary"
                                        class="align-bottom"
                                        @click="updateFilter"
                                        >Filtern</b-button
                                    >
                                </template>
                            </b-input-group>
                        </b-form-group>
                    </b-col>
                </b-row>
            </section>
            <Table
                bordered
                striped
                small
                showFilter
                :fields="fields"
                :items="items"
                :isBusy="busy"
                sortBy="id"
                busyText="Lädt Kategorien..."
                emptyText="Keine Kategorien gefunden"
            >
                <template v-slot:groups="{ row: row }">
                    <template v-if="row.value.length > 0">
                        <b-badge
                            v-for="group in row.value"
                            :key="group"
                            href="/fibu/group"
                            class="mx-1 my-1"
                            >{{ findGroup(group) }}</b-badge
                        >
                    </template>
                    <template v-else
                        ><span class="small font-italic"
                            >Keine Gruppe zugeordnet</span
                        ></template
                    >
                </template>
                <template v-slot:actions="{ row: row }">
                    <b-button
                        v-if="checkRight('write')"
                        :size="size"
                        variant="link"
                        @click="dataSelected(row)"
                        class="mv-table-action-btn mr-1"
                        >Bearbeiten</b-button
                    >
                    <b-button
                        v-if="checkRight('delete')"
                        :size="size"
                        @click="dataDelete(row)"
                        variant="link"
                        class="text-danger mv-table-action-btn"
                        >Löschen</b-button
                    >
                </template>
            </Table>
            <EditModal
                :title="selected.title"
                :busy="selected.busy"
                buttonText="Speichern"
                scrollable
                @ok="dataProvide"
                @hide="dataHide"
            >
                <template v-if="selected.data">
                    <FormGroupBuilder
                        :fields="ioFields"
                        :data="selected.data"
                        :refresh="validationRefresh"
                    />
                </template>
            </EditModal>
            <RemoveModal
                :title="selected.title"
                :busy="selected.busy"
                @ok="dataProvide"
                @hide="dataHide"
            />
        </div>
    </MainContainer>
</template>

<script>
import CommonView from "@/mixins/CommonView/CommonView";
import Size from "@/mixins/Size/Size";
import Validation from "@/mixins/Validation/Validation";
import { ApiFactory } from "@/api/ApiFactory.js";
import MainContainer from "@/components/MainContainer/MainContainer";
import Table from "@/components/Table/Table";
import EditModal from "@/components/EditModal/EditModal";
import RemoveModal from "@/components/RemoveModal/RemoveModal";
import FormGroupBuilder from "@/components/FormGroupBuilder/FormGroupBuilder";

const FiBuCategoryApi = ApiFactory.get("fibuCategory");
const FiBuGroupApi = ApiFactory.get("fibuGroup");

export default {
    name: "FiBu_Category",
    mixins: [CommonView, Size, Validation],
    components: {
        MainContainer,
        Table,
        EditModal,
        RemoveModal,
        FormGroupBuilder
    },
    data() {
        return {
            fields: [
                {
                    key: "id",
                    label: "ID",
                    sortable: true
                },
                {
                    key: "groups",
                    label: "Gruppe",
                    sortable: true
                },
                {
                    key: "description",
                    label: "Beschreibung",
                    sortable: true
                },
                {
                    key: "actions",
                    label: "Funktionen",
                    class: "text-center"
                }
            ],
            ioFields: {
                id: {
                    key: "id",
                    label: "ID",
                    type: "text",
                    mandatory: true,
                    disabled: true
                },
                groups: {
                    key: "groups",
                    label: "Gruppe",
                    type: "checkboxselect",
                    options: [],
                    mandatory: false,
                    selectSize: 5
                },
                description: {
                    key: "description",
                    label: "Beschreibung",
                    type: "text",
                    mandatory: true
                }
            },
            api: FiBuCategoryApi,
            groups: {
                busy: false,
                items: [],
                options: [],
                raw: []
            },
            filter: {
                _group_id: null
            }
        };
    },
    watch: {
        "groups.items": {
            handler(val) {
                this.ioFields.groups.options = val;
            }
        }
    },
    created() {
        this.fetchGroups();
    },
    methods: {
        async fetch(busyState = true) {
            this.busy = busyState;
            const response = await this.api.get({ filter: this.filter });
            if (response.ok) {
                var items = response.json.collection;
                items = items.map(item => this.process(item));
                this.items = items;
                this.registerRights(response);
            }
            this.busy = false;
        },
        async fetchGroups() {
            this.groups.busy = true;
            const response = await FiBuGroupApi.get();
            if (response.ok) {
                this.groups.items = response.json.collection.map(item => {
                    return {
                        value: item.id,
                        text: item.description
                    };
                });
                this.groups.options = response.json.collection.map(item => {
                    return {
                        value: item.id,
                        text: item.description
                    };
                });
                this.groups.options.unshift({ value: null, text: "Alle" });
                this.groups.raw = response.json.collection;
            }
            this.groups.busy = false;
        },
        findGroup(id) {
            const result = this.groups.raw.find(el => el.id === id);
            if (result) {
                return `${result.description} (${result.slug})`;
            } else {
                return "";
            }
        },
        updateFilter() {
            this.fetch();
        },
        postProcessCreate(selectedItem, response) {
            selectedItem.id = response.json.id;
            return selectedItem;
        },
        fetchErrorHandling(response) {
            this.handleValidationData(response, this.ioFields);
        },
        postDataSet() {
            this.cleanValidationData(this.ioFields);
        }
    }
};
</script>
